import type { BalanceRegistration } from '@sportninja/common/services/teamBalance'
import type {
  BalanceCandidateTableRow,
  BalanceSheetRow,
  BalanceTableRow,
} from '../types/balanceGrid'
import {
  candidateMatchesRowFilter,
  matchesRowFilter,
  type BalanceRowFilter,
} from './balanceRowFilter'
import { orderSheetRows } from './balanceRowOrder'
import { activeOfficialPayers, candidateRowKey } from './balanceRowKind'

/**
 * The per-registration facts every row of that registration carries — built ONCE
 * per registration, so its rows share one object rather than a copy each.
 */
export const toSheetRegistration = (
  registration: BalanceRegistration
): BalanceTableRow['registration'] => ({
  id: registration.id,
  auto: registration.auto,
  locked: registration.locked,
  currency: registration.currency,
  division: registration.division,
  // James, Jul 31 — the pinned Player Share total quotes the fee these
  // shares divide and the server's own unallocated gap.
  team_cost: registration.team_cost,
  kpis: registration.kpis,
  // D48 — per-registration so the flattened All-Divisions view gates
  // the "List in Player Finder…" kebab entry row by row.
  can_list_in_player_finder: registration.can_list_in_player_finder ?? false,
  // TF-03 — the name cell's "Ad Hidden" names why a listed slot's ad is hidden.
  player_finder_hidden_reasons: registration.player_finder_hidden_reasons,
  player_finder_recruiting: registration.player_finder_recruiting,
  // Jeff, Oct 1 — gates "Invite Player" row by row. An older api2 sends nothing
  // and has no invite endpoints, so nothing is offered.
  can_invite_players: registration.can_invite_players ?? false,
})

interface BuildSheetRowsInput {
  registrations: readonly BalanceRegistration[]
  showCancelled: boolean
  rowFilter: BalanceRowFilter
}

/**
 * The sheet's rows, in display order.
 *
 * Per registration, never across the flattened result: All Divisions still reads
 * division by division, each division's block starting with its own team reps
 * (SN-8467), then its players in join order — see `orderSheetRows` for the bands.
 *
 * Sep 23 — each registration's team officials who have no line yet join as $0
 * candidate rows, under "All" only, in the team reps band. A candidate whose person
 * already has an ACTIVE line is dropped even when that line is filtered out of view:
 * the server never sends one, and a stale payload must not show the same person
 * twice.
 */
export const buildSheetRows = ({
  registrations,
  showCancelled,
  rowFilter,
}: BuildSheetRowsInput): BalanceSheetRow[] =>
  registrations.flatMap((registration) => {
    const sheetRegistration = toSheetRegistration(registration)

    const slotRows: BalanceSheetRow[] = registration.slots
      .filter((slot) => showCancelled || slot.status !== 'cancelled')
      .filter((slot) => matchesRowFilter(slot, rowFilter))
      .map((slot) => ({ key: slot.id, slot, registration: sheetRegistration }))

    const withLines = activeOfficialPayers(registration)
    const candidateRows: BalanceCandidateTableRow[] = candidateMatchesRowFilter(
      rowFilter
    )
      ? (registration.official_candidates ?? [])
          .filter((candidate) => !withLines.has(candidate.payer_user_id))
          .map((candidate) => ({
            key: candidateRowKey(registration.id, candidate.payer_user_id),
            candidate,
            registration: sheetRegistration,
          }))
      : []

    return orderSheetRows([...slotRows, ...candidateRows])
  })
